'use client';

import React, { useState, forwardRef, useRef, memo, useEffect } from 'react';
import isEqual from 'lodash-es/isEqual';
import { MediaUploading } from 'app/libs/useMediaManager';
import {
    getDynamicImageSource,
    getS3ObjectUrl,
    getStaticImageSource,
} from 'app/libs/url';
import { type BasicShopUIPropTypes } from 'shop-ui/types';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS } from 'app/shop-ui/constants';
import getHTML, { DOMPurify } from '@shared/get-html';
import Link from 'next/link';
import './Media.scss';

const defaultImageUrl = getS3ObjectUrl(
    '/static/default-image-upload-placeholder-small.jpg'
);

interface MediaPropsBasicPropTypes {
    id: string;
    mediaSource?: string;
    defaultMediaSource?: string;
    autoPlay?: boolean;
    muted?: boolean;
    aspectRatio?: string;
    onLoad?: () => void;
}

interface MediaPropsPropTypes
    extends MediaPropsBasicPropTypes,
        Omit<BasicShopUIPropTypes, 'id'> {
    isUploading: boolean;
    updateLastChangeTime: () => void;
}

export const MediaPicture: React.FC<MediaPropsBasicPropTypes> = (props) => {
    const imageRef = useRef<HTMLImageElement>(null);

    const { mediaSource, onLoad, defaultMediaSource, aspectRatio } = props;

    const defaultSource = defaultMediaSource
        ? getStaticImageSource(defaultMediaSource)
        : getStaticImageSource(defaultImageUrl);

    const source = mediaSource ? getDynamicImageSource(mediaSource) : defaultSource;

    useEffect(() => {
        function checkImageBrowserCache() {
            const img = new Image();

            if (source?.[0]?.srcSet) {
                img.srcset = source?.[0]?.srcSet;
            }

            if (source.src) {
                img.src = source.src;
            }

            img.onload = () => {
                onLoad?.();
            };
        }

        checkImageBrowserCache();
    }, [source]);

    return (
        <picture
            className="shop-ui-media-picture"
            style={{
                ...(aspectRatio && {
                    '--shop-ui-custom-media-ratio': aspectRatio,
                }),
            }}
        >
            <source srcSet={source.srcSet} type={source.type} />
            <img
                ref={imageRef}
                className="shop-ui-media-image"
                src={source.src}
                alt="media"
                draggable={false}
                onLoad={onLoad}
            />
        </picture>
    );
};

export const ExternalMediaPicture: React.FC<MediaPropsBasicPropTypes> = (props) => {
    const { mediaSource, onLoad } = props;

    const imageRef = useRef<HTMLImageElement>(null);

    const [isError, setIsError] = useState(false);

    useEffect(() => {
        function checkImageBrowserCache() {
            const img = new Image();

            if (mediaSource) {
                img.src = mediaSource;
            }

            img.onload = () => {
                onLoad?.();
            };
        }

        checkImageBrowserCache();
    }, [mediaSource]);

    if (isError) {
        return <div className="shop-ui-media-image-error"></div>;
    }

    return (
        <img
            ref={imageRef}
            className="shop-ui-media-image"
            src={mediaSource}
            alt="media"
            onLoad={() => {
                onLoad?.();
            }}
            onError={() => {
                setIsError(true);
            }}
            draggable={false}
        />
    );
};

const _MediaVideo: React.FC<MediaPropsBasicPropTypes> = (props) => {
    const { mediaSource, autoPlay = false, muted = false, onLoad } = props;

    const videoRef = useRef<HTMLVideoElement>(null);

    return (
        <video
            ref={videoRef}
            className="shop-ui-media-video"
            src={mediaSource}
            muted={muted}
            autoPlay={autoPlay}
            playsInline
            loop
            draggable={false}
            onLoadedData={onLoad}
        >
            <track kind="captions" src={mediaSource} label="English" />
        </video>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function propsAreEqual(prevProps: any, nextProps: any) {
    if (prevProps.isUploading !== nextProps.isUploading) {
        return false;
    }

    return (
        // 影片 re-render 時，會重新載入影片，導致畫面閃爍
        (prevProps.type === ELEMENT_TYPE.VIDEO &&
            nextProps.type === ELEMENT_TYPE.VIDEO &&
            prevProps.autoPlay === nextProps.autoPlay &&
            prevProps.muted === nextProps.muted &&
            prevProps.mediaSource === nextProps.mediaSource &&
            prevProps.readyToEdit === nextProps.readyToEdit &&
            isEqual(prevProps.style, nextProps.style)) ||
        // 圖片 resizing 時，允許 re-render，保持圖片比例
        (prevProps.type === ELEMENT_TYPE.IMAGE &&
            nextProps.type === ELEMENT_TYPE.IMAGE &&
            prevProps.mediaSource === nextProps.mediaSource &&
            prevProps.mediaMaskColor === nextProps.mediaMaskColor &&
            isEqual(prevProps.style, nextProps.style))
    );
}

export const MediaVideo = memo(_MediaVideo, propsAreEqual);

const _Media = forwardRef<HTMLDivElement, MediaPropsPropTypes>((props, ref) => {
    const {
        id,
        type,
        mediaSource,
        defaultMediaSource,
        style,
        mediaMaskColor,
        styleMap,
        autoPlay,
        muted,
        isUploading,
        updateLastChangeTime,
    } = props;

    const [isLoading, setIsLoading] = useState(true);

    const LinkComponent = ({ children }: { children: React.ReactNode }) => {
        if (styleMap && styleMap[FEATURE_FLAG_KEYS.HYPERLINK]) {
            const target = styleMap[FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW]
                ? '_blank'
                : '_self';

            const href = DOMPurify.sanitize(styleMap[FEATURE_FLAG_KEYS.HYPERLINK]);

            return (
                <Link href={href} target={target}>
                    {children}
                </Link>
            );
        }

        return <>{children}</>;
    };

    const isExternalURL =
        styleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'externalImage' ||
        styleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'externalVideo';

    if (isUploading) {
        return (
            <div
                ref={ref}
                style={style}
                className="shop-ui-media shop-ui-media--uploading"
            >
                <MediaUploading />
            </div>
        );
    }

    function handleLoad() {
        setIsLoading(false);
        updateLastChangeTime?.();
    }

    return (
        <div ref={ref} style={style} className="shop-ui-media">
            {isLoading && <div className="skeleton" />}
            <LinkComponent>
                {type === ELEMENT_TYPE.IMAGE && mediaSource && !isExternalURL && (
                    <MediaPicture
                        id={id}
                        mediaSource={mediaSource}
                        aspectRatio={styleMap?.[FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]}
                        onLoad={handleLoad}
                    />
                )}
                {type === ELEMENT_TYPE.IMAGE && mediaSource && isExternalURL && (
                    <ExternalMediaPicture
                        id={id}
                        mediaSource={mediaSource}
                        onLoad={handleLoad}
                    />
                )}
                {type === ELEMENT_TYPE.VIDEO && mediaSource && !isExternalURL && (
                    <MediaVideo
                        id={id}
                        mediaSource={mediaSource}
                        autoPlay={autoPlay}
                        muted={muted}
                        onLoad={handleLoad}
                    />
                )}
                {type === ELEMENT_TYPE.VIDEO &&
                    mediaSource &&
                    isExternalURL &&
                    getHTML(
                        mediaSource,
                        'div',
                        {
                            className: 'shop-ui-media-video',
                        },
                        { dangerouslyDisableDomPurify: true }
                    )}
                {!mediaSource && (
                    <MediaPicture
                        id={id}
                        defaultMediaSource={defaultMediaSource}
                        onLoad={handleLoad}
                    />
                )}
                {mediaMaskColor && (
                    <div
                        className="shop-ui-media-mask"
                        style={{ '--shop-ui-media-mask-color': mediaMaskColor }}
                    />
                )}
            </LinkComponent>
        </div>
    );
});

_Media.displayName = 'Media';

export const Media = memo(_Media, propsAreEqual);
